import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { formatBillNaira } from '@/components/bills/billPaymentUtils';
import { Loader } from '@/components/ui/Loader';
import { cn } from '@/utils/cn';

export interface BillPlanOption {
  value: string;
  label: string;
  price: number;
  leadingIcon?: ReactNode;
  subtitle?: string;
}

interface BillPlanPickerProps {
  label?: string;
  placeholder?: string;
  value: string;
  options: BillPlanOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  loading?: boolean;
  sheetTitle?: string;
}

/** Otobill-style plan select: dropdown on desktop, bottom sheet on mobile. */
export function BillPlanPicker({
  label,
  placeholder = 'Choose a plan',
  value,
  options,
  onChange,
  disabled = false,
  loading = false,
  sheetTitle,
}: BillPlanPickerProps) {
  const [open, setOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value) ?? null;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleSelect(next: string) {
    onChange(next);
    setOpen(false);
    setSheetOpen(false);
  }

  function openPicker() {
    if (disabled || loading || options.length === 0) return;
    if (window.innerWidth < 768) {
      setSheetOpen(true);
    } else {
      setOpen((prev) => !prev);
    }
  }

  const listContent = loading ? (
    <div className="flex items-center justify-center p-4">
      <Loader label="Loading plans..." />
    </div>
  ) : options.length === 0 ? (
    <div className="p-4 text-sm text-gray-500">{placeholder}</div>
  ) : (
    <div className="p-2">
      {options.map((option) => {
        const isSelected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => handleSelect(option.value)}
            className={cn(
              'flex w-full items-center gap-3 rounded-lg p-3 text-left transition-colors hover:bg-gray-50',
              isSelected && 'bg-secondary-50',
            )}
          >
            {option.leadingIcon}
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium text-gray-900">{option.label}</div>
              {option.subtitle ? (
                <div className="truncate text-xs text-gray-500">{option.subtitle}</div>
              ) : null}
            </div>
            <div className="shrink-0 text-sm font-semibold text-brand-600">
              {formatBillNaira(option.price)}
            </div>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-2">
      {label && <label className="block text-sm font-medium text-gray-700">{label}</label>}

      <div className="relative" ref={containerRef}>
        <button
          type="button"
          disabled={disabled || loading || options.length === 0}
          onClick={openPicker}
          className={cn(
            'flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white p-3 text-left transition hover:bg-gray-50',
            'focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20',
            (disabled || loading || options.length === 0) && 'cursor-not-allowed opacity-50',
          )}
        >
          <div className="flex min-w-0 items-center gap-3">
            {selected ? (
              <>
                {selected.leadingIcon}
                <div className="min-w-0 text-left">
                  <div className="truncate font-medium text-gray-900">{selected.label}</div>
                  <div className="text-sm text-gray-500">{formatBillNaira(selected.price)}</div>
                </div>
              </>
            ) : (
              <span className="text-gray-500">
                {loading ? 'Loading plans...' : placeholder}
              </span>
            )}
          </div>
          <ChevronDown
            className={cn('h-4 w-4 shrink-0 text-gray-500 transition-transform', open && 'rotate-180')}
          />
        </button>

        {open && (
          <div className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg">
            {listContent}
          </div>
        )}
      </div>

      {sheetOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close"
            onClick={() => setSheetOpen(false)}
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[75vh] overflow-hidden rounded-t-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <h3 className="font-semibold text-gray-900">{sheetTitle ?? label ?? 'Select plan'}</h3>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="rounded-lg p-1 text-gray-500 hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="max-h-[calc(75vh-3.5rem)] overflow-y-auto">{listContent}</div>
          </div>
        </div>
      )}
    </div>
  );
}
